import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type FormEvent,
} from "react";
import { toast } from "sonner";
import { isAxiosError } from "axios";
import { Button } from "@/components/ui/button";
import { EnrichmentCard } from "./EnrichmentCard";
import { EnrichmentSessionSetup } from "./EnrichmentSessionSetup";
import { ReservationWindow } from "./ReservationWindow";
import { SessionBatchList } from "./SessionBatchList";
import { SessionExpiryBanner } from "./SessionExpiryBanner";
import { useEnrichmentSession } from "@/hooks/useEnrichmentSession";
import { getContact } from "@/api/contacts";
import { completeEnrichmentWork, type WorkCompleteRequest } from "@/api/enrichment";
import { logAction } from "@/api/activity";
import { MAWorkSelectionPanel, MAWorkDiagnostic } from "./MAWorkSelectionPanel";
import type { MAWorkCount, MAWorkView } from "@/api/enrichment";
import type { Contact } from "@/api/types";

interface EnrichmentWorkProps {
  slug: string;
  /** False while the tab is mounted but hidden. Both queue tabs stay
   *  mounted so neither loses its batch on a tab switch, which means both
   *  would otherwise answer the same window-level keyboard shortcuts —
   *  one "S" would skip a row in each. */
  active: boolean;
  /** Rows the work queue can hand out right now. Null while unknown. */
  queueCount: number | null;
  /** Re-read that counter. The page owns it because one endpoint serves
   *  both queues and both tabs are mounted at once. */
  onQueueChanged: () => void;
  selectionCounts?: Record<string, MAWorkCount> | null;
}

/** Work uses one atomic completion request. The server derives changed fields,
 *  saves the contact, routes the company, records earnings evidence and releases
 *  the reservation in the same transaction. */
export function EnrichmentWork({
  slug,
  active,
  queueCount,
  onQueueChanged,
  selectionCounts,
}: EnrichmentWorkProps) {
  // The reserved batch IS the queue: we no longer read the global
  // qualified list, so two operators can never be handed the same row.
  const [workView, setWorkView] = useState<MAWorkView>("WORK");
  const { session, isOpening, openError, isEnding, open, end, skip } =
    useEnrichmentSession(slug, "work", onQueueChanged, workView);
  // Memoised: `?? []` would hand back a fresh array on every render and
  // invalidate everything downstream that depends on the queue.
  const queue = useMemo(() => session?.leads ?? [], [session]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [todayCount, setTodayCount] = useState(0);
  const [startTime, setStartTime] = useState<number>(Date.now());

  // Full primary contact (the preview on Company strips title /
  // phone / linkedin_url so we'd otherwise blank-overwrite them).
  const [primaryContact, setPrimaryContact] = useState<Contact | null>(null);

  // Form state
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [title, setTitle] = useState("");
  const [linkedin, setLinkedin] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const savingRef = useRef(false);
  const completionRef = useRef<{
    signature: string;
    request: WorkCompleteRequest;
  } | null>(null);

  const firstNameRef = useRef<HTMLInputElement>(null);

  const current = queue[currentIndex];

  /** Opening a session resets the cursor: the batch is a fresh list. */
  const startSession = useCallback(
    async (count: Parameters<typeof open>[0]) => {
      setCurrentIndex(0);
      await open(count);
    },
    [open],
  );

  // When the cursor moves, fetch the full primary contact (the queue
  // row only carries the trimmed preview) and reset the form to the
  // pre-filled values.
  useEffect(() => {
    if (!current) {
      setPrimaryContact(null);
      return;
    }
    const previewId = current.primary_contact?.id ?? null;
    setStartTime(Date.now());
    if (!previewId) {
      setPrimaryContact(null);
      resetFormFromContact(null, current.primary_contact?.full_name ?? "");
      window.setTimeout(() => firstNameRef.current?.focus(), 50);
      return;
    }
    let cancelled = false;
    setPrimaryContact(null);
    getContact(slug, previewId)
      .then((c) => {
        if (cancelled) return;
        setPrimaryContact(c);
        resetFormFromContact(c, current.primary_contact?.full_name ?? "");
        window.setTimeout(() => firstNameRef.current?.focus(), 50);
      })
      .catch(() => {
        if (cancelled) return;
        // 404 / network — treat as no existing contact and let the
        // operator type from scratch.
        resetFormFromContact(null, current.primary_contact?.full_name ?? "");
        window.setTimeout(() => firstNameRef.current?.focus(), 50);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, current?.id]);

  function resetFormFromContact(c: Contact | null, fallbackFullName: string) {
    const fullName = c?.full_name ?? fallbackFullName;
    const parts = (fullName ?? "").trim().split(/\s+/).filter(Boolean);
    setFirstName(parts[0] ?? "");
    setLastName(parts.slice(1).join(" "));
    setTitle(c?.title ?? "");
    setLinkedin(c?.linkedin_url ?? "");
    setPhone(c?.phone ?? "");
    setEmail(c?.email ?? "");
  }

  function advance() {
    completionRef.current = null;
    setCurrentIndex((i) => i + 1);
  }

  async function handleSubmit(e?: FormEvent) {
    if (e) e.preventDefault();
    if (savingRef.current || !current) return;
    savingRef.current = true;
    setIsSaving(true);
    try {
      const fields = {
        contact_id: primaryContact?.id ?? null,
        expected_contact_updated_at: primaryContact?.updated_at ?? null,
        full_name: `${firstName} ${lastName}`.trim() || primaryContact?.full_name || "",
        title: title.trim() || null,
        linkedin_url: linkedin.trim() || null,
        phone: phone.trim() || null,
        email: email.trim() || null,
      };
      const signature = JSON.stringify({ companyId: current.id, ...fields });
      if (completionRef.current?.signature !== signature) {
        completionRef.current = {
          signature,
          request: {
            ...fields,
            request_id: crypto.randomUUID(),
            time_spent_seconds: Math.max(1, Math.round((Date.now() - startTime) / 1000)),
          },
        };
      }
      const result = await completeEnrichmentWork(slug, current.id, completionRef.current.request);
      if (result.routing_skipped_reason) toast.info("Contact saved. Prospecting remains blocked pending review.");

      setTodayCount((n) => n + 1);
      advance();
    } catch (err) {
      const detail: unknown = isAxiosError(err) ? err.response?.data?.detail : null;
      toast.error(
        typeof detail === "string"
          ? detail
          : "Couldn't complete the enrichment. Your form is kept; retry to confirm the result.",
      );
       
      console.warn("[Enrichment] submit failed", err);
    } finally {
      savingRef.current = false;
      setIsSaving(false);
    }
  }

  /** The one release the frontend has to make by hand. Saving a lead or
   *  logging "can't find" writes an enrichment_completed activity, and the
   *  backend frees the row in that same transaction — calling skip there
   *  too would release a row that is already released. */
  function handleSkip() {
    if (savingRef.current || !current) return;
    void skip(current.id);
    advance();
  }

  async function handleCantFind() {
    if (savingRef.current || !current) return;
    savingRef.current = true;
    setIsSaving(true);
    try {
      await logAction(slug, current.id, {
        action: "enrichment_completed",
        details: {
          changes: [],
          result: "not_found",
          time_spent_seconds: Math.max(
            1,
            Math.round((Date.now() - startTime) / 1000),
          ),
        },
      });
      advance();
    } catch (err) {
      toast.error("Couldn't record 'Can't find'. The company is still open; try again.");
      console.warn("[Enrichment] cant-find log failed", err);
    } finally {
      savingRef.current = false;
      setIsSaving(false);
    }
  }

  // Keyboard: S = skip, X = can't find. Enter is handled by the
  // form's submit, so we don't intercept it when typing in an input.
  useEffect(() => {
    if (!active) return;
    const handler = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      if (
        target instanceof HTMLInputElement ||
        target instanceof HTMLTextAreaElement ||
        target?.isContentEditable
      ) {
        return;
      }
      if (e.key === "s" || e.key === "S") handleSkip();
      else if (e.key === "x" || e.key === "X") void handleCantFind();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, currentIndex, current?.id]);

  // Pre-open Google "site:linkedin.com/in" searches for the next 3
  // leads in new tabs — handy for the multi-tab workflow.
  function openNextLinkedInSearches() {
    const upcoming = queue.slice(currentIndex + 1, currentIndex + 4);
    let opened = 0;
    for (const c of upcoming) {
      const contact = c.primary_contact;
      if (!contact?.full_name) continue;
      const q = encodeURIComponent(
        `"${contact.full_name}" "${c.name}" site:linkedin.com/in`,
      );
      window.open(`https://www.google.com/search?q=${q}`, "_blank");
      opened += 1;
    }
    if (opened === 0) toast.info("No upcoming leads to pre-search");
  }

  const queueAhead = useMemo(
    () => queue.slice(currentIndex + 1, currentIndex + 4),
    [queue, currentIndex],
  );

  // Keyed on the batch, not the cursor: a 500-row lot would otherwise
  // rebuild this list on every save.
  const batchListItems = useMemo(
    () =>
      queue.map((c) => ({
        key: c.id,
        primary: c.name,
        secondary: c.primary_contact?.full_name ?? null,
      })),
    [queue],
  );

  const contactMeta = (primaryContact?.extra_metadata ??
    current?.primary_contact?.metadata ??
    {}) as Record<string, unknown>;
  const dirigeantAge =
    typeof contactMeta.age === "number" ? contactMeta.age : null;
  const dirigeantTenure =
    typeof contactMeta.tenure_years === "number"
      ? contactMeta.tenure_years
      : null;

  if (!session) {
    return (
      <div className="space-y-4">
      {selectionCounts && <MAWorkSelectionPanel slug={slug} counts={selectionCounts} view={workView} onView={setWorkView} />}
      {(!selectionCounts || workView !== "BLOCKED") && <EnrichmentSessionSetup
        title={selectionCounts ? (workView === "MANUAL_EXCEPTION" ? "Historical manual admissions" : "Admitted Work session") : "New enrichment session"}
        description="Reserve a batch of admitted records. Mandate diagnostics do not certify qualification. While you hold them, no other operator is served the same rows."
        isOpening={isOpening}
        error={openError}
        onStart={(n) => void startSession(n)}
        queueCount={selectionCounts ? (selectionCounts[workView]?.available ?? 0) : queueCount}
        accent="indigo"
      />}
      </div>
    );
  }

  // Two different endings, and the operator needs to tell them apart:
  // an empty batch means come back later, a finished one means done.
  if (queue.length === 0) {
    return (
      <div className="mx-auto max-w-lg py-20 text-center">
        <div className="mb-4 text-6xl">🗒️</div>
        <h2 className="text-xl font-bold text-slate-900">
          Nothing to enrich
        </h2>
        <p className="mt-2 text-slate-500">
          {session.queue_remaining > 0
            ? `Nothing was free to reserve just now — the ${session.queue_remaining} compan${session.queue_remaining === 1 ? "y" : "ies"} left in the queue are being worked by other operators.`
            : "The qualified queue is empty. Nothing is waiting to be enriched right now."}
        </p>
        <p className="mt-1 text-sm text-slate-400">
          Come back later, or try a smaller batch.
        </p>
        <Button
          onClick={() => void end()}
          className="mt-6"
          disabled={isEnding}
        >
          Back to setup
        </Button>
      </div>
    );
  }

  if (!current || currentIndex >= queue.length) {
    const reserved = queue.length;
    return (
      <div className="mx-auto max-w-lg py-20 text-center">
        <div className="mb-4 text-6xl">✅</div>
        <h2 className="text-xl font-bold text-slate-900">Session complete</h2>
        <p className="mt-2 text-slate-500">
          {reserved} compan{reserved === 1 ? "y" : "ies"} processed,{" "}
          {todayCount} enriched.
        </p>
        <p className="mt-1 text-sm text-slate-400">
          {session.queue_remaining} still waiting in the queue.
        </p>
        <Button
          onClick={() => void end()}
          className="mt-6"
          disabled={isEnding}
        >
          End session
        </Button>
      </div>
    );
  }

  const linkedinSearchQuery = encodeURIComponent(
    `"${firstName} ${lastName}".trim() "${current.name}" site:linkedin.com/in`,
  );

  return (
    <div className="mx-auto max-w-[1400px] space-y-4">
      <SessionExpiryBanner expiresAt={session.expires_at} />
      {session.work_diagnostics?.[current.id] && <MAWorkDiagnostic selection={session.work_diagnostics[current.id]} />}
      <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
        <div className="flex flex-wrap items-center gap-3">
          <span className="text-slate-500">
            {currentIndex + 1} / {queue.length} in batch
          </span>
          {/* Rows still free for OTHER operators, straight from the
            *  server. Not the size of this batch, and not a total. */}
          <span className="text-xs text-slate-400">
            {session.queue_remaining} left in queue
          </span>
          {/* Sized from the lot server-side, so this reads 3h on a small
            *  batch and up to a day on a large one. */}
          <ReservationWindow expiresAt={session.expires_at} />
        </div>
        <div className="flex items-center gap-3">
          <span className="font-medium text-indigo-600">
            Today: {todayCount}
          </span>
          <button
            type="button"
            onClick={openNextLinkedInSearches}
            className="text-xs text-slate-400 hover:text-indigo-600"
            title="Open next 3 LinkedIn searches in new tabs"
          >
            🔍 Pre-open next 3
          </button>
          <button
            type="button"
            onClick={() => void end()}
            disabled={isEnding}
            className="text-xs text-slate-400 hover:text-rose-600 disabled:opacity-50"
            title="Release the rows you have not worked yet"
          >
            End session
          </button>
        </div>
      </div>
      <div className="h-1.5 overflow-hidden rounded-full bg-slate-100">
        <div
          className="h-full rounded-full bg-indigo-500 transition-all duration-300"
          style={{ width: `${(currentIndex / queue.length) * 100}%` }}
        />
      </div>

      {/* Card and batch list side by side once there is room for both. A
        *  lot can be 500 rows now: the operator has to see where they are
        *  in it without scrolling the page away from the form they are
        *  typing into, so the list scrolls inside its own column and the
        *  card stays where it is. */}
      <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_22rem]">
        <div className="mx-auto w-full min-w-0 max-w-3xl space-y-4">
          <EnrichmentCard
            mode="enrichment"
            companyName={current.name}
            taxId={current.tax_id}
            city={current.city}
            postalCode={current.postal_code}
            leaderName={
              primaryContact?.full_name ?? current.primary_contact?.full_name ?? null
            }
            leaderAge={dirigeantAge}
            leaderTenure={dirigeantTenure}
            firstName={firstName}
            lastName={lastName}
            onFirstName={setFirstName}
            onLastName={setLastName}
            title={title}
            onTitle={setTitle}
            linkedin={linkedin}
            onLinkedin={setLinkedin}
            phone={phone}
            onPhone={setPhone}
            email={email}
            onEmail={setEmail}
            linkedinSearchQuery={linkedinSearchQuery}
            autoFocusRef={firstNameRef}
            isSaving={isSaving}
            canSubmit
            onSubmit={handleSubmit}
            onSkip={handleSkip}
            onCantFind={() => void handleCantFind()}
          />

          {/* Superseded by the batch list on wide screens — the same
            *  information, more of it — so it only renders where the rail
            *  doesn't fit. */}
          {queueAhead.length > 0 && (
            <div className="rounded-xl border border-slate-900/[0.06] bg-slate-50/50 p-3 xl:hidden">
              <div className="mb-2 text-[10px] font-medium uppercase text-slate-400">
                Next up
              </div>
              {queueAhead.map((c) => {
                const contact = c.primary_contact;
                const cMeta = (contact?.metadata ?? {}) as Record<string, unknown>;
                const age = typeof cMeta.age === "number" ? cMeta.age : null;
                const hasLinkedinPreview = !!contact?.email;
                const searchHref =
                  contact?.full_name &&
                  `https://www.google.com/search?q=${encodeURIComponent(
                    `"${contact.full_name}" "${c.name}" site:linkedin.com/in`,
                  )}`;
                return (
                  <div
                    key={c.id}
                    className="flex items-center justify-between py-1.5 text-sm"
                  >
                    <div className="flex min-w-0 items-center gap-2">
                      <span className="truncate text-slate-600">{c.name}</span>
                      <span className="truncate text-xs text-slate-400">
                        · {contact?.full_name ?? "—"}
                        {age != null && ` · ${age} ans`}
                      </span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      {hasLinkedinPreview ? (
                        <span className="text-[10px] text-emerald-500">
                          ✓ contact info
                        </span>
                      ) : searchHref ? (
                        <a
                          href={searchHref}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="rounded bg-indigo-50 px-1.5 py-0.5 text-[10px] text-indigo-500 hover:text-indigo-700"
                        >
                          🔍 Search
                        </a>
                      ) : null}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="hidden xl:block">
          <SessionBatchList
            items={batchListItems}
            currentIndex={currentIndex}
            accent="indigo"
          />
        </div>
      </div>
    </div>
  );
}
